import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Minus, Plus, Check, Loader2, Sparkles, Flag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib`;

const norm = (s) => (s || '').toLowerCase().replace(/[”“]/g, '"').replace(/[’‘]/g, "'");

// Small violet "AUTO" pill — the crib's rules will drop this in with what's ticked (hover = why)
export const AutoTag = ({ qty, a, testid }) => (
  <span className="ml-1 inline-flex items-center gap-0.5 align-middle rounded bg-violet-600 text-white px-1 py-px text-[9px] font-bold uppercase tracking-wide" title={`${a.rule} — comes with ${(a.for || []).join(', ')}`} data-testid={testid}>
    <Sparkles className="w-2.5 h-2.5" />auto{qty > 1 ? ` ×${qty}` : ''}
  </span>
);

// Small amber flag — something to think about, NOT added (e.g. wedge anchors picked → hammer drill / matching bit?)
export const FlagTag = ({ h, testid }) => (
  <span className="ml-1 inline-flex items-center gap-0.5 align-middle rounded bg-amber-500 text-white px-1 py-px text-[9px] font-bold uppercase tracking-wide whitespace-nowrap" title={h.message} data-testid={testid}>
    <Flag className="w-2.5 h-2.5" />{h.short || 'check'}
  </span>
);

// The paper tool list as a scrollable tick-sheet: category → subgroup → items in the same order as the printed
// sheet. Tap an item to tick it (qty 1), use − / + for more. `cart` / `bump` come from KitItemPicker so the
// footer "Add to kit" and the Search tab share one basket. `inKit` = catalog_id → qty already on the kit.
// `auto` = catalog_id → {qty, rule, for}: companions the rules will add for the current picks (shown live in violet).
// `hints` = catalog_id → {short, message}: flagged for the picker to consider (amber), nothing is added.
// `filterItem(item)` keeps only matching items (e.g. consumables for a timesheet); `extraSections` are prepended as-is.
export default function KitSheetList({ cart, bump, inKit = {}, auto = {}, hints = {}, query = '', hideStock = false, className = '', filterItem = null, extraSections = null, testidPrefix = 'kit-sheet' }) {
  const [sheet, setSheet] = useState(null);
  const [active, setActive] = useState(null);
  const scroller = useRef(null);
  const secRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/catalog/sheet`).then((r) => {
      let secs = (r.data.sections || []).filter((s) => !hideStock || !/^Shop stock/i.test(s.category));
      if (filterItem) secs = secs.map((s) => ({ ...s, groups: s.groups.map((g) => ({ ...g, items: g.items.filter(filterItem) })).filter((g) => g.items.length) })).filter((s) => s.groups.length);
      setSheet([...(extraSections || []), ...secs]);
    }).catch(() => setSheet([...(extraSections || [])]));
  }, [hideStock, extraSections]);

  const q = norm(query.trim());
  const sections = useMemo(() => {
    const words = q ? q.split(/\s+/) : [];
    if (!sheet) return [];
    if (!words.length) return sheet;
    return sheet.map((s) => ({
      ...s,
      groups: s.groups.map((g) => ({
        ...g,
        items: g.items.filter((it) => { const hay = norm(`${s.category} ${g.subgroup} ${it.label || ''} ${it.name || ''} ${it.part_number || ''}`); return words.every((w) => hay.includes(w)); }),
      })).filter((g) => g.items.length),
    })).filter((s) => s.groups.length);
  }, [sheet, q]);

  const jump = (cat) => {
    setActive(cat);
    secRefs.current[cat]?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  // highlight the category in view
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onScroll = () => {
      let cur = null;
      for (const s of sections) {
        const node = secRefs.current[s.category];
        if (node && node.offsetTop - el.scrollTop <= 60) cur = s.category;
      }
      if (cur) setActive(cur);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [sections]);

  if (!sheet) return <div className="flex-1 flex items-center justify-center text-slate-500 text-sm py-10"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading the list…</div>;
  if (!sections.length) return <p className="text-sm text-slate-500 px-4 py-6">Nothing on the sheet matches “{query}”. Try a shorter word, or add it as a Purchased / Fabricated item.</p>;

  return (
    <div className={`flex-1 min-h-0 flex ${className}`} data-testid={testidPrefix}>
      {/* category rail */}
      <nav className="hidden sm:flex flex-col w-40 flex-shrink-0 border-r border-slate-200 overflow-y-auto py-2" data-testid="kit-sheet-rail">
        {sections.map((s) => {
          const picked = s.groups.reduce((n, g) => n + g.items.filter((it) => cart[it.catalog_id]?.qty).length, 0);
          return (
            <button key={s.category} onClick={() => jump(s.category)} data-testid={`kit-sheet-jump-${s.category.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              className={`text-left px-3 py-1.5 text-[12px] leading-tight border-l-2 ${active === s.category ? 'border-blue-600 text-blue-800 font-semibold bg-blue-50' : 'border-transparent text-slate-600 hover:bg-slate-50'}`}>
              {s.category}{picked > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold">{picked}</span>}
            </button>
          );
        })}
      </nav>

      {/* the sheet */}
      <div ref={scroller} className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 relative" data-testid="kit-sheet-scroll">
        {sections.map((s) => (
          <section key={s.category} ref={(el) => { secRefs.current[s.category] = el; }} className="pt-3" data-testid={`kit-sheet-section-${s.category.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
            <h4 className="sticky top-0 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4 py-1.5 bg-white text-slate-900 text-xs font-bold uppercase tracking-wide">{s.category}</h4>
            {s.groups.map((g) => (
              <div key={g.subgroup || '_'} className="grid grid-cols-[92px_1fr] sm:grid-cols-[128px_1fr] gap-x-2 py-1.5 border-b border-slate-100">
                <div className="text-[11px] sm:text-xs font-semibold text-slate-500 pt-2 leading-tight">{g.subgroup}</div>
                <div className="flex flex-wrap gap-1.5">
                  {g.items.map((it) => {
                    const qty = cart[it.catalog_id]?.qty || 0;
                    const have = inKit[it.catalog_id] || 0;
                    const au = !qty && auto[it.catalog_id];
                    const hi = !qty && !au && hints[it.catalog_id];
                    return (
                      <div key={it.catalog_id} className={`inline-flex items-stretch rounded-lg border text-sm select-none ${qty ? 'border-blue-600 bg-blue-600 text-white' : au ? 'border-violet-400 bg-violet-50 text-violet-900' : hi ? 'border-amber-400 bg-amber-50 text-amber-900 ring-2 ring-amber-200' : have ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-slate-300 bg-white text-slate-800 hover:border-slate-400'}`} data-testid={`kit-sheet-item-${it.catalog_id}`}>
                        <button onClick={() => bump(it, qty ? -qty : (au ? Math.max(1, Math.round(au.qty)) : (it.default_qty || 1)))} className="px-2.5 py-1.5 text-left flex items-center gap-1.5 min-h-[36px]" title={au ? `${au.rule} — comes with ${(au.for || []).join(', ')} (tap to put it on the list yourself)` : hi ? hi.message : have ? `Already in kit ×${have} — tap to add more` : qty ? 'Tap to untick' : 'Tap to tick'} data-testid={`kit-sheet-toggle-${it.catalog_id}`}>
                          {qty ? <Check className="w-3.5 h-3.5 flex-shrink-0" /> : have ? <span className="text-[10px] font-bold text-emerald-700 flex-shrink-0">✓{have}</span> : null}
                          <span className="leading-tight">{it.name}</span>
                          {au && <AutoTag qty={au.qty} a={au} testid={`kit-sheet-auto-${it.catalog_id}`} />}
                          {hi && <FlagTag h={hi} testid={`kit-sheet-flag-${it.catalog_id}`} />}
                          {it.unit_cost != null && <span className={`text-[10px] ${qty ? 'text-blue-800' : 'text-slate-500'}`}>${Number(it.unit_cost).toFixed(2)}</span>}
                        </button>
                        {qty > 0 && (
                          <span className="flex items-center border-l border-slate-200">
                            <button onClick={() => bump(it, -1)} className="px-1.5 h-full hover:bg-slate-900/10" data-testid={`kit-sheet-minus-${it.catalog_id}`}><Minus className="w-3.5 h-3.5" /></button>
                            <input type="number" inputMode="numeric" min="0" step="1" value={qty} onChange={e => { if (e.target.value === '') return; bump(it, Math.max(0, Math.floor(Number(e.target.value) || 0)) - qty); }} onFocus={e => e.target.select()} onClick={e => e.stopPropagation()}
                              className="w-10 h-full text-center font-bold text-sm bg-slate-900/10 text-slate-900 outline-none focus:bg-white focus:text-slate-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" title="Type the quantity" data-testid={`kit-sheet-qty-${it.catalog_id}`} />
                            <button onClick={() => bump(it, 1)} className="px-1.5 h-full hover:bg-slate-900/10" data-testid={`kit-sheet-plus-${it.catalog_id}`}><Plus className="w-3.5 h-3.5" /></button>
                            {it.kind === 'consumable' && [10, 25, 50].map(n => (
                              <button key={n} onClick={() => bump(it, n - qty)} className={`px-1.5 h-full text-[10px] font-bold border-l border-slate-200 hover:bg-slate-900/10 ${qty === n ? 'bg-slate-900/10' : ''}`} title={`Set to ${n}`} data-testid={`kit-sheet-quick-${n}-${it.catalog_id}`}>{n}</button>
                            ))}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
